import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Stack } from 'new/elements/Stack';
import { ResourceSearch } from 'new/elements/ResourceSearch';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { Activity } from 'new/elements/Activity';
import { fetchAuditLog } from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_AUDIT_LOG_REQUEST } from 'js/constants/actionTypes';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_MONITOR } from 'new/constants/pathnames';
import { ModalFlowPreview } from 'new/elements/ModalFlowPreview';
import { Resume } from 'new/components/monitor/ResourceTriage/components/Resume';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { resourceFetchActions } from 'new/components/monitor/ResourceTriage/ResourceTriage';
import { BannerDescription } from 'new/elements/BannerDescription';
import useAppDispatch from 'js/hooks/useAppDispatch';

import styles from './AuditLog.module.scss';

interface AuditLogProps {}

export const Audit: ScreenComponent<AuditLogProps> = () => {
  const history = useHistory();
  const initialParams = new URLSearchParams(history.location.search);
  const [data, setData] = useState({
    type: initialParams.get('type'),
    id: initialParams.get('id'),
  });
  const dispatch = useAppDispatch();
  const [triedLoad, setTried] = useState(false);
  const loading = useIsLoading(GET_AUDIT_LOG_REQUEST);
  const { resource } = useResource(data.type, data.id);
  const description = 'View events for a Resource.';

  useEffect(() => {
    onSearch(data);
  }, []);

  const updateUrl = (data) => {
    const params = new URLSearchParams(history.location.search);
    Object.keys(data).forEach((key) => {
      const value = data[key];
      params.set(key, typeof value === 'object' ? value.id : value);
    });
    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  };

  const onSearch = (data) => {
    if (data.type) {
      setData({ type: '', id: '' });
      const action = resourceFetchActions[data.type];
      if (action) {
        setTried(true);
        dispatch(
          action(data.id, {
            aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
            ignoreErrors: true,
          }),
        ).then(() => {
          setData(data);
          dispatch(fetchAuditLog(data.type, data.id));
        });
      }
      updateUrl(data);
    }
  };

  useEffect(() => {
    return () => {
      setTried(false);
    };
  }, []);

  return (
    <div className={styles.container}>
      <BannerDescription title="Audit" description={description} />
      <Stack flex={1} height="100%">
        <Stack className={styles.top}>
          <Stack className={styles.topContent}>
            <Stack
              direction="row"
              paddingRight="32px"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <ResourceSearch
                onSearch={onSearch}
                noRequest
                hideOptions={['flows']}
                type={initialParams?.get('type') || ''}
                id={initialParams?.get('id') || ''}
              />
            </Stack>
            {loading ? <CenteredLoader absolute /> : null}
          </Stack>
        </Stack>
        <Stack direction="row" flex={1}>
          <Stack className={styles.content}>
            <Activity
              preventRequest
              data={data}
              resourceType={data.type}
              resourceId={Number(data.id)}
              triedLoad={triedLoad}
            />
          </Stack>
          <Stack width="300px" minWidth="300px">
            {!!resource || loading ? (
              <Resume
                {...resource}
                loading={loading}
                name={resource?.name}
                resourceType={data.type}
                id={data.id}
                disableFlowSelection
              />
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};

Audit.screenConfig = {
  screenName: 'Audit',
  domain: 'monitor',
  modals: [ModalFlowPreview],
  path: `${ROUTE_REDESIGN}${ROUTE_MONITOR}/audit`,
};
